import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, RefreshCw, RotateCcw, X } from "lucide-react";
import { Button } from "@bookdental/ui";

type Props = {
  onCapture: (dataUrl: string) => Promise<void> | void;
  onCancel: () => void;
  saving?: boolean;
};

export function CameraCapture({ onCapture, onCancel, saving }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [captured, setCaptured] = useState<string | null>(null);
  const [facing, setFacing] = useState<"user" | "environment">("user");
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      setStarting(true);
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facing,
            width: { ideal: 720 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
        setError(null);

        // Detecta múltiplas câmeras (pra botão "alternar")
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cams = devices.filter((d) => d.kind === "videoinput");
        if (!cancelled) setHasMultipleCameras(cams.length > 1);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Erro ao acessar câmera";
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setStarting(false);
      }
    }

    if (!captured) start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [facing, captured]);

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = 480;
    canvas.height = 480;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // Center crop quadrado
    const size = Math.min(v.videoWidth, v.videoHeight);
    const sx = (v.videoWidth - size) / 2;
    const sy = (v.videoHeight - size) / 2;
    ctx.drawImage(v, sx, sy, size, size, 0, 0, 480, 480);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setCaptured(dataUrl);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const retake = () => setCaptured(null);

  if (error) {
    return (
      <div className="space-y-3 py-4 text-center">
        <p className="text-sm text-red-600">{error}</p>
        <p className="text-xs text-zinc-500">
          Verifique a permissão de câmera no navegador e se nenhum outro app está usando.
        </p>
        <Button variant="outline" onClick={onCancel}>
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative aspect-square overflow-hidden rounded-lg bg-zinc-900">
        {captured ? (
          <img src={captured} alt="Captura" className="h-full w-full object-cover" />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="h-full w-full object-cover"
            />
            {starting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={captured ? retake : onCancel}>
          {captured ? (
            <>
              <RotateCcw className="h-4 w-4" />
              Tirar outra
            </>
          ) : (
            <>
              <X className="h-4 w-4" />
              Cancelar
            </>
          )}
        </Button>

        {!captured && hasMultipleCameras && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))}
          >
            <RefreshCw className="h-4 w-4" />
            Alternar
          </Button>
        )}

        {captured ? (
          <Button size="sm" onClick={() => onCapture(captured)} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {saving ? "Salvando..." : "Usar foto"}
          </Button>
        ) : (
          <Button size="sm" onClick={capture} disabled={starting}>
            <Camera className="h-4 w-4" />
            Capturar
          </Button>
        )}
      </div>
    </div>
  );
}
